// src/pages/SubscriptionsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Avatar,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  theme,
} from "antd";
import { SearchOutlined, SwapOutlined, PlusOutlined, SettingOutlined, QuestionCircleOutlined, RightCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  type LicenseRow,
  useLicensesActions,
  useLicensesState,
} from "../../reducers/subscription";
import { useNavigate } from "react-router";
import BuyLicense from "../buyLicense/BuyLicense";
import { useLanguage } from "src/providers/LanguageProvider";

const { Title, Text } = Typography;

/* ——— Helpers ——— */
const currency = (n: number | string | null | undefined) =>
  new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(Number(n || 0));



const WManagement = ({ item }: { item: LicenseRow }) => {
  const { t } = useLanguage();

  if (item.licenseProductId == '13') {
    if (item.wCompanyInfo && item.wCompanyInfo?.status == 'completed') {
      return (
        <Flex vertical align="start" justify="start">
          <Text style={{ marginTop: '2px' }} type="secondary">{item.wCompanyInfo.CompanyName + ' ' + item.wCompanyInfo.CompanyAddress}</Text>{" "}
          <Button
            style={{ marginLeft: '-5px' }}
            type="link"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => window.location.href = `https://myaccount.parts-soft.eu/w-management/?company_id=${item.Id}`}
          >
            {t("BTN_W_MANAGE")}
          </Button>
        </Flex>
      );
    } else if (item.wCompanyInfo && item.wCompanyInfo?.status == 'uncompleted') {
      return (
        <Flex vertical align="start" justify="start">
          <Text style={{ marginTop: '2px' }} type="secondary">{item.wCompanyInfo.CompanyName + ' ' + item.wCompanyInfo.CompanyAddress}</Text>{" "}

          <Button
            style={{ marginLeft: '-10px' }}
            type="link"
            size="small"
            icon={<RightCircleOutlined />}
            onClick={() => window.location.href = `https://myaccount.parts-soft.eu/w-management/wizard?licenseId=${item.Id}`}
          >
            {t("CONTINUE_INSTALLATION")}
          </Button>
        </Flex>
      );
    } else {
      return (
        <Flex vertical align="start" justify="start">

          <Text type="secondary" style={{ marginTop: '2px' }}>
            {t("NO_ASSIGN_W_COMPANY")}
          </Text>
          <Button
            style={{ marginLeft: '-10px' }}
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => window.location.href = `https://myaccount.parts-soft.eu/w-management/wizard?licenseId=${item.Id}`}
          >
            {t("CREATE_WCOMPANY")}
          </Button>
        </Flex>
      );
    }
  }

  return null
};




export default function SubscriptionsPage() {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { t } = useLanguage();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | LicenseRow["status"]>("all");
  const [addModalOpen, setAddModalOpen] = useState(false);

  const { data, loading /* , selected, error */ } = useLicensesState();
  const { load, setSelectedRow, setManagementDrawerOpen } = useLicensesActions();

  const [selectedProduct, setSelectedProduct] = useState<null | { Id: string; Price: number; IsMonthly: boolean }>(null);

  const navigate = useNavigate();

  useEffect(() => {
    load();
  }, []);

  // filtre (sadece üst seviyeye uygulanır)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((r) => {
      const matchesText =
        !q ||
        r.service?.toLowerCase().includes(q) ||
        r.plan?.toLowerCase().includes(q);
      const matchesStatus = status === "all" ? true : r.status === status;
      return matchesText && matchesStatus;
    });
  }, [data, query, status]);

  const statusMeta: Record<
    LicenseRow["status"],
    { color: "success" | "default"; text: string }
  > = {
    active: { color: "success", text: t("STATUS_ACTIVE") },
    canceled: { color: "default", text: t("STATUS_CANCELED") },
  };

  /* ——— Table columns ——— */
  const columns: ColumnsType<LicenseRow> = [
    {
      title: t("COL_SERVICE"),
      key: "service",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <Avatar shape="square" src={r.img} size={36} />
          <div style={{ minWidth: 0 }}>
            <Text strong ellipsis style={{ display: "block", maxWidth: 280 }}>
              {r.service || r.plan} - ({r.Id})
            </Text>
            <Text
              type="secondary"
              ellipsis
              style={{ display: "block", maxWidth: 280, fontSize: 12 }}
            >
              <WManagement item={r} />
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: t("COL_AMOUNT"),
      key: "amount",
      width: 180,
      align: "right",
      render: (_, r) => {
        const hasChildren = r.amount !== r.totalAmount;
        if (!hasChildren) {
          return (
            <Text>
              {currency(r.amount)} <Text type="secondary">/ {r.interval}</Text>
            </Text>
          );
        }
        return (
          <>
            <Text type="secondary">
              {currency(r.amount)} <Text type="secondary">/ {r.interval}</Text>
            </Text>
            <br />
            {r.totalAmount ? (
              <Text strong>
                {currency(r.totalAmount)} <Text type="secondary">/ {r.interval}</Text>
              </Text>
            ) : null}
          </>
        );
      },
      responsive: ["sm"],
    },
    {
      title: t("COL_NEXT_RENEWAL"),
      key: "renewal",
      width: 180,
      render: (_, r) => <Text type="secondary">{r.nextRenewal || "-"}</Text>,
      responsive: ["md"],
    },
    {
      title: t("COL_STATUS"),
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (v: LicenseRow["status"]) => (
        <Tag bordered={false} color={statusMeta[v]?.color}>
          {statusMeta[v]?.text}
        </Tag>
      ),
    },
    {
      title: t("COL_ACTIONS"),
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, r) =>
        r.isChild ? null : (
          <Space>
            <Button
              size="middle"
              type="default"
              icon={<SwapOutlined />}
              onClick={() => {
                setSelectedRow(r.Id);
                setManagementDrawerOpen(true); // global drawer kontrolü
              }}
            >
              <span className="hide-sm">{t("BTN_DETAIL")}</span>
            </Button>
          </Space>
        ),
    },
  ];

  return (
    <div>
      <BuyLicense
        mainLicense={null}
        onCancel={() => {
          setAddModalOpen(false);
          setSelectedProduct(null);
        }}
        open={addModalOpen}
        onSubmit={() => {
          setAddModalOpen(false);
          setSelectedProduct(null);
          load();
        }}
      />

      <Card
        title={
          <Title level={3} style={{ margin: 0, fontWeight: 700 }}>
            {t("MY_SUBSCRIPTIONS")}
          </Title>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
          >
            {t("BTN_NEW_SUBSCRIPTION")}
          </Button>
        }
        headStyle={{
          padding: "12px 16px",
          borderBottom: "1px solid " + token.colorSplit,
        }}
        bodyStyle={{ padding: 16 }}
      >
        <Flex gap={8} wrap style={{ marginBottom: 8 }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t("SEARCH_SERVICE_OR_PLAN")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 360 }}
            size="middle"
          />
          <Select
            value={status}
            onChange={setStatus}
            style={{ width: 200 }}
            size="middle"
            options={[
              { value: "all", label: t("FILTER_ALL_STATUSES") },
              { value: "active", label: t("STATUS_ACTIVE") },
              { value: "canceled", label: t("STATUS_CANCELED") },
            ]}
          />
        </Flex>

        <Table<LicenseRow>
          rowKey="key"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <>
                    <div>{t("TABLE_EMPTY_TITLE")}</div>
                    <Text type="secondary">
                      {t("TABLE_EMPTY_HINT")}
                    </Text>
                  </>
                }
              />
            ),
          }}
        />
      </Card>
    </div>
  );
}
